import React from "react";
import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { binencode } from "@/lib/binary";
import {
  MessageBodyType,
  MessageDestinationType,
  MessagePacketType,
  WiredMessage,
  WiredMessageBody,
  WiredMessagePacket,
  type MessageEntity,
} from "@/moby/db";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { ed25519 } from "@noble/curves/ed25519";
import {
  CheckIcon,
  DownloadIcon,
  ExternalLinkIcon,
  LoaderCircleIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  RotateCcwIcon,
  XIcon,
} from "lucide-react";
import { toast } from "sonner";
import type bin from "typed-binary";
import { useLocation } from "wouter";
import { useMobyDB, useMobyNodeClient, useSession } from "../provider";
import { formatBytes } from "@/lib/number";
import Dexie from "dexie";
import { getPeerAvatarColor, renderPeerAvatarEmoji } from "@/lib/peer-profile-utils";
import { getFileFromOPFS, triggerStreamingDownload } from "@/lib/file";
import {
  TransferControlButtons,
  TransferStateBadge,
  TransferProgressIndicator,
  TransferInfoDisplay,
  getStateLabel,
  useSafeFileTransferWithExtension,
  useAllPeersFileTransfers
} from "../file/shared-transfer-components";
import { type FileTransferRecord } from "@/lib/file-transfer-types";
import { FileTransferStateEnum } from "@/lib/file-transfer-types";

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|ico|avif)$/i;

function isImageFile(filename: string): boolean {
  return IMAGE_EXTENSIONS.test(filename);
}

// FIX 9a: Derive MIME type from file extension for typed Blob creation.
// OPFS File objects have type: "" which causes Firefox to fail rendering
// blob URLs in <img> tags (Firefox is stricter about content-type sniffing).
const IMAGE_MIME_MAP: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp',
  ico: 'image/x-icon', avif: 'image/avif',
};
function getImageMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return IMAGE_MIME_MAP[ext] || 'application/octet-stream';
}

function getPeerImageRendering(peerAddress: string): boolean {
  return localStorage.getItem(`moby_peer_renderImages_${peerAddress}`) === 'true';
}

/**
 * Reactive hook for per-chat image rendering preference.
 * Listens for 'moby-render-images-changed' custom events so the UI
 * re-renders instantly when the toggle is flipped — from the chat panel
 * header OR the edit-contact dialog.
 */
function usePeerImageRendering(address: string): boolean {
  const [enabled, setEnabled] = React.useState(
    () => localStorage.getItem(`moby_peer_renderImages_${address}`) === 'true'
  );

  React.useEffect(() => {
    setEnabled(localStorage.getItem(`moby_peer_renderImages_${address}`) === 'true');
  }, [address]);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.address === address) {
        setEnabled(detail.enabled);
      }
    };
    window.addEventListener('moby-render-images-changed', handler);
    return () => window.removeEventListener('moby-render-images-changed', handler);
  }, [address]);

  return enabled;
}

export function ChatMessageCard({
  messageSignature,
}: {
  messageSignature: string;
}) {
  const db = useMobyDB();
  const session = useSession();

  const message = useSuspenseLiveQuery(async () => {
    return await db.messages.get({ signature: messageSignature });
  }, [messageSignature]);

  const attachments = useSuspenseLiveQuery(async () => {
    if (message === undefined) {
      return [];
    }
    if (message.attachments === undefined) {
      return [];
    }

    const attachments = await db.fileMetadatas
      .where("hash")
      .anyOf(message.attachments)
      .toArray();

    return await Promise.all(
      attachments.map(async (attachment) => {
        return Object.entries(attachment.entries).map(([root, path]) => {
          return {
            hash: attachment.hash,
            root,
            path,
          };
        });
      }),
    ).then((attachments) => attachments.flat());
  }, [messageSignature]);

  const contact = useSuspenseLiveQuery(async () => {
    if (message === undefined) {
      return;
    }

    if (message.from.address === session?.address) {
      return {
        address: session.address,
        displayName: session.name,
        initials: session.name.slice(0, 2),
      };
    }

    const contact = await db.contacts.get({ address: message.from.address });
    if (contact === undefined) {
      return;
    }

    return {
      address: contact.address,
      displayName: contact.getDisplayName(),
      initials: contact.getInitials(),
      custom: contact.custom,
    };
  }, [message?.from.address, session?.address, session?.name]);

  if (contact === undefined || session === undefined || message === undefined) {
    return null;
  }

  if (message.type === MessageBodyType.GROUP_JOIN) {
    return (
      <div className="group text-on-surface-variant text-label-sm grid place-items-center">
        {message.from.address === session.address &&
          "You have joined the group."}
        {message.from.address !== session.address &&
          `${contact.displayName} joined the group.`}
      </div>
    );
  }

  if (message.type === MessageBodyType.GROUP_LEAVE) {
    return (
      <div className="group text-on-surface-variant text-label-sm grid place-items-center">
        {message.from.address === session.address && "You have left the group."}
        {message.from.address !== session.address &&
          `${contact.displayName} left the group.`}
      </div>
    );
  }
  return (
    <div
      data-user={message.from.address === session.address || undefined}
      className="group flex justify-start data-user:justify-end"
    >
      {message.from.address !== session.address && (
        <div className="relative mr-2 size-9 shrink-0">
          <div 
            className="grid size-9 place-items-center rounded-full text-white"
            style={{ backgroundColor: getPeerAvatarColor(contact.custom?.profile) }}
          >
            <div className="text-label-md">{contact.initials}</div>
            {renderPeerAvatarEmoji(contact.custom?.profile, "sm")}
          </div>
        </div>
      )}
      <div className="max-w-[70%]">
        {message.groupInvite !== undefined && (
          <GroupInviteCard groupInvite={message.groupInvite} />
        )}
        {attachments !== undefined &&
          attachments.length > 0 &&
          attachments.map((attachment) => (
            <AttachmentCard
              key={attachment.root}
              hash={attachment.hash}
              root={attachment.root}
              path={attachment.path}
              senderAddress={message.from.address}
              isOwnMessage={message.from.address === session.address}
              chatAddress={message.chatAddress}
            />
          ))}
        <div className="rounded-medium text-body-lg group-data-user:bg-secondary group-data-user:text-on-secondary bg-surface-container-high text-on-surface group-data-user:rounded-bl-medium rounded-br-medium rounded-bl-none p-3 group-data-user:rounded-br-none">
          {message.text}
        </div>
        <div
          className={`text-label-sm text-on-surface-variant mt-1 text-start group-data-user:text-end`}
        >
          {message.timestamp.toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function AttachmentCard({
  root,
  path,
  senderAddress,
  isOwnMessage = false,
  chatAddress,
}: {
  hash: string;
  root: string;
  path: string;
  senderAddress: string;
  isOwnMessage?: boolean;
  chatAddress?: string;
}) {
  const db = useMobyDB();
  // CRITICAL FIX: Use aggregated transfers to prevent context loss in multi-peer scenarios
  // useFileTransfer() only sees current peer's transfers, causing Brazil/California/Mumbai routing issues
  const aggregated = useAllPeersFileTransfers();
  const fileTransfer = {
    activeTransfers: aggregated.activeTransfers,
    queuedTransfers: {
      send: aggregated.sendQueue,
      receive: aggregated.receiveQueue
    },
    completedTransfers: aggregated.completedTransfers,
    startTransfer: aggregated.startTransfer,
    pauseTransfer: aggregated.pauseTransfer,
    resumeTransfer: aggregated.resumeTransfer,
    cancelTransfer: aggregated.cancelTransfer
  };

  // Reactive image rendering preference for this chat
  // Use chatAddress (peer addr for DMs, group addr for groups) for consistent per-chat control
  const renderAddress = chatAddress || senderAddress;
  const peerRendersImages = usePeerImageRendering(renderAddress);
  
  // Look up both the file and its transfer state
  const file = useSuspenseLiveQuery(async () => {
    const foundFile = await db.files.get({ root });
    return foundFile;
  }, [root]);

  // Check if there's an active transfer for this file
  const transfer = React.useMemo(() => {
    // FIX C: Search completedTransfers FIRST so COMPLETED state wins over stale
    // sendQueue/receiveQueue entries that may linger after disconnect recovery.
    // Previous order (active → send → receive → completed) caused .find() to return
    // a stale "Metadata Sent" entry instead of the COMPLETED one.
    const completed = [...(fileTransfer.completedTransfers?.values() || [])];
    const allTransfers = [...fileTransfer.activeTransfers.values()];
    const queuedSend = [...fileTransfer.queuedTransfers.send.values()];
    const queuedReceive = [...fileTransfer.queuedTransfers.receive.values()];
    const allPossibleTransfers = [...completed, ...allTransfers, ...queuedSend, ...queuedReceive];
    
    // CRITICAL FIX: Search by multiple strategies since attachment hash may not match transfer hash
    // 1. Try exact root hash match (works when hashes align)
    // 2. Try matching by filename (works when metadata hash differs from file hash)
    const found = allPossibleTransfers.find(t => {
      // Strategy 1: Direct hash match (includes attachmentHash bridge for large files)
      if (t.rootHash === root || t.metadata?.rootHash === root || t.metadata?.attachmentHash === root) {
        return true;
      }
      
      // Strategy 2: Match by filename if path is available
      // This handles OPFS transfers where metadata hash differs from actual file hash
      if (path && t.metadata?.name === path) {
        return true;
      }
      
      return false;
    });
    
    return found;
  }, [fileTransfer.activeTransfers, fileTransfer.queuedTransfers, fileTransfer.completedTransfers, root, path]);

  // Get the filename - prioritize transfer metadata, then path parameter
  const fileName = React.useMemo(() => {
    if (transfer?.metadata?.name) {
      return transfer.metadata.name;
    }
    // Use the path parameter which contains the original filename
    if (path && path !== '') {
      return path;
    }
    // Fallback to hash prefix
    return `file_${root.slice(0, 8)}`;
  }, [transfer, path, root]);

  // Calculate progress for active transfers from lightweight merkle tree counter
  const progress = React.useMemo(() => {
    if (!transfer || !transfer.metadata) return 0;
    
    // LIGHTWEIGHT: Read counter from merkle tree (enriched at source by getActiveTransfers)
    const completed = transfer.metadata.completedChunks || 0;
    const total = transfer.metadata.totalChunks || 0;
    
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  }, [transfer]);

  // Determine if the file is ready for download
  const isFileReady = React.useMemo(() => {
    // File must exist and have actual downloaded content
    if (!file || file.size === 0) {
      return false;
    }
    
    // CRITICAL FOR SAME-MACHINE TESTING:
    // If there's an active or queued transfer, use its state as the source of truth
    // This prevents receiver from seeing sender's downloaded=1 status
    if (transfer) {
      if (transfer.state === FileTransferStateEnum.COMPLETED) return true;
      // FAST-TRANSFER FIX: If transfer state is stale (5s poll delay) but DB already
      // reflects completion, accept file.downloaded as secondary indicator.
      // Only trust this for inbound transfers to avoid same-machine sender false positive.
      if (transfer.metadata?.direction === 'inbound' && file.downloaded === 1) return true;
      return false;
    }
    
    // No transfer record - check file downloaded status
    // This handles old completed transfers
    if (file.downloaded !== 1) {
      return false;
    }
    
    // File is downloaded (downloaded === 1) and no active transfer
    return true;
  }, [file, transfer]);

  // Handle starting a queued transfer
  const handleStartTransfer = async () => {
    if (!transfer || !transfer.sessionId) {
      console.warn('[AttachmentCard] No transfer to start');
      return;
    }
    
    try {
      await fileTransfer.startTransfer(transfer.sessionId);
    } catch (error) {
      console.error('[AttachmentCard] Failed to start transfer:', error);
    }
  };

  // AUTO-START: For inbound image transfers when Render Images is ON for this peer,
  // skip the manual "Start" button and begin transfer immediately.
  const autoStartAttempted = React.useRef(false);
  React.useEffect(() => {
    if (autoStartAttempted.current) return;
    if (!transfer || !transfer.sessionId) return;

    const isQueued = transfer.state === FileTransferStateEnum.IDLE ||
                     transfer.state === FileTransferStateEnum.METADATA_RECEIVED ||
                     transfer.state === FileTransferStateEnum.READY_FOR_TRANSFER;
    const isReceiver = transfer.metadata?.direction === 'inbound';
    const isImage = isImageFile(fileName);

    if (isQueued && isReceiver && isImage && peerRendersImages) {
      autoStartAttempted.current = true;
      handleStartTransfer();
    }
  }, [transfer, fileName, senderAddress]);

  // Resolve group peer addresses to contact display names
  const [peerNames, setPeerNames] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    if (!transfer?.metadata?.isGroupTransfer) return;
    const addresses = new Set<string>();
    transfer.metadata.groupReceiverProgress?.forEach((_, addr) => addresses.add(addr));
    transfer.metadata.groupSeeders?.forEach((_, addr) => addresses.add(addr));
    if (addresses.size === 0) return;
    
    (async () => {
      const names: Record<string, string> = {};
      for (const addr of addresses) {
        try {
          const contact = await db.contacts.get({ address: addr });
          names[addr] = contact ? contact.getDisplayName() : addr.slice(0, 8) + '…';
        } catch {
          names[addr] = addr.slice(0, 8) + '…';
        }
      }
      setPeerNames(names);
    })();
  }, [transfer?.metadata?.isGroupTransfer, transfer?.metadata?.groupReceiverProgress?.size, transfer?.metadata?.groupSeeders?.size]);

  // Handle pause/resume actions
  const handlePauseTransfer = async () => {
    if (!transfer || !transfer.sessionId) return;
    try {
      await fileTransfer.pauseTransfer(transfer.sessionId);
    } catch (error) {
      console.error('[AttachmentCard] Failed to pause transfer:', error);
    }
  };

  const handleResumeTransfer = async () => {
    if (!transfer || !transfer.sessionId) return;
    try {
      await fileTransfer.resumeTransfer(transfer.sessionId);
    } catch (error) {
      console.error('[AttachmentCard] Failed to resume transfer:', error);
    }
  };

  // Show transfer state with appropriate actions
  if (transfer) {
    const isQueued = transfer.state === FileTransferStateEnum.IDLE || 
                     transfer.state === FileTransferStateEnum.METADATA_RECEIVED ||
                     transfer.state === FileTransferStateEnum.READY_FOR_TRANSFER;
    const isPaused = transfer.state === FileTransferStateEnum.PAUSED;
    const isTransferring = transfer.state === FileTransferStateEnum.TRANSFERRING ||
                           transfer.state === FileTransferStateEnum.METADATA_PENDING ||
                           transfer.state === FileTransferStateEnum.METADATA_SENT ||
                           transfer.state === FileTransferStateEnum.PREPARING;

    // Show PAUSED state with resume button
    if (isPaused) {
      const pausedBy = transfer.custom?.pausedBy;
      return (
        <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-surface-container-high shadow-sm">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-orange-100">
              <ExternalLinkIcon className="h-4 w-4 text-orange-600" />
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-medium text-on-surface">{fileName}</div>
              <div className="text-xs text-on-surface-variant">
                {formatBytes(file?.size || transfer.metadata?.size || 0)} • 
                {progress > 0 ? `Resume from ${progress}%` : 'Ready to resume'} •
                {pausedBy === 'local' ? ' Paused by you' : pausedBy === 'peer' ? ' Paused by peer' : ' Paused'}
              </div>
            </div>
          </div>
          {/* Only show resume button if paused by local user */}
          {pausedBy !== 'peer' ? (
            <button
              onClick={handleResumeTransfer}
              className="flex items-center gap-1 px-3 py-1 bg-primary text-on-primary rounded-md hover:bg-primary/90 text-sm"
            >
              <PlayIcon className="h-3 w-3" />
              Resume
            </button>
          ) : (
            <span className="text-xs text-on-surface-variant">
              Waiting for peer...
            </span>
          )}
        </div>
      );
    }

    if (isQueued) {
      // SENDER_START_FIX: Only receivers should see "Start" button for queued transfers
      // Senders should see "Metadata Sent" or "Waiting for receiver" status
      const isReceiver = transfer.metadata?.direction === 'inbound';
      const isSender = transfer.metadata?.direction === 'outbound';
      
      // IMAGE AUTO-START: If this is an inbound image with Render Images ON,
      // the useEffect auto-start will fire — show "Starting..." instead of "Start" button
      const isAutoStartImage = isReceiver && isImageFile(fileName) && peerRendersImages;
      
      return (
        <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-surface-container-high shadow-sm">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
              <ExternalLinkIcon className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-medium text-on-surface">{fileName}</div>
              <div className="text-xs text-on-surface-variant">
                {formatBytes(file?.size || transfer.metadata?.size || 0)} • {isAutoStartImage ? 'Auto-starting...' : isReceiver ? 'Ready to transfer' : 'Metadata Sent'}
              </div>
            </div>
          </div>
          {isAutoStartImage ? (
            <div className="flex items-center gap-1 text-xs text-primary">
              <LoaderCircleIcon className="h-3 w-3 animate-spin" />
              <span>Starting</span>
            </div>
          ) : isReceiver ? (
            <button
              onClick={handleStartTransfer}
              className="flex items-center gap-1 px-3 py-1 bg-primary text-on-primary rounded-md hover:bg-primary/90 text-sm"
            >
              <PlayIcon className="h-3 w-3" />
              Start
            </button>
          ) : (
            <span className="text-xs text-on-surface-variant px-2 py-1 bg-surface-container rounded">
              Waiting for receiver...
            </span>
          )}
        </div>
      );
    }

    // Show FINALIZING state (chunks received, writing to OPFS)
    if (transfer.state === FileTransferStateEnum.FINALIZING) {
      return (
        <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-surface-container-high shadow-sm">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
              <ExternalLinkIcon className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-medium text-on-surface">{fileName}</div>
              <div className="text-xs text-on-surface-variant">
                {formatBytes(file?.size || transfer.metadata?.size || 0)} • Finalizing...
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LoaderCircleIcon className="h-4 w-4 animate-spin text-primary" />
          </div>
        </div>
      );
    }
    
    // Show FAILED/ERROR state with rejection reason
    if (transfer.state === FileTransferStateEnum.FAILED || transfer.state === FileTransferStateEnum.ERROR) {
      const errorMessage = transfer.errorMessage || transfer.metadata?.error || 'Transfer failed';
      return (
        <div className="flex items-center justify-between gap-2 p-3 rounded-lg border border-error/30 bg-error-container/30 shadow-sm">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-error/10">
              <XIcon className="h-4 w-4 text-error" />
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-medium text-on-surface">{fileName}</div>
              <div className="text-xs text-error" title={errorMessage}>
                {formatBytes(file?.size || transfer.metadata?.size || 0)} • {errorMessage.length > 40 ? errorMessage.slice(0, 40) + '...' : errorMessage}
              </div>
            </div>
          </div>
          <span className="text-xs font-medium text-error px-2 py-1 bg-error/10 rounded">
            Failed
          </span>
        </div>
      );
    }
    
    // Show COMPLETED state — render inline image if enabled, otherwise download card
    // CRITICAL: Without this branch, COMPLETED transfers fall through the entire if(transfer) block
    // and hit "Waiting for metadata..." when db.files root hash doesn't match attachment hash
    // (common for sender-side large files where metadata hash ≠ Merkle root)
    // GROUP SENDER SKIP: Group outbound transfers fall through to the dedicated
    // GROUP SENDER COMPLETED card (below the if(transfer) block) which shows
    // per-receiver progress bars and "Serving" status instead of a generic P2P card.
    if (transfer.state === FileTransferStateEnum.COMPLETED
        && !(transfer.metadata?.isGroupTransfer && transfer.metadata?.direction === 'outbound')) {
      const transferSize = file?.size || transfer.metadata?.size || 0;
      const completedOpfsRoot = transfer.rootHash || root;
      
      const handleCompletedDownload = async () => {
        try {
          const opfsFile = await getFileFromOPFS(completedOpfsRoot);
          if (!opfsFile) {
            console.warn('[AttachmentCard] Completed file not found in OPFS', { root: completedOpfsRoot.slice(0, 8) });
            return;
          }
          const url = URL.createObjectURL(opfsFile);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('[AttachmentCard] Download failed:', error);
        }
      };

      // FIX: Check inline image rendering here — the normal shouldRenderImage path
      // at the bottom of AttachmentCard is unreachable because this COMPLETED branch
      // returns early. Use transfer.rootHash for OPFS lookup (Merkle root ≠ content hash).
      if (isImageFile(fileName) && peerRendersImages) {
        return (
          <InlineImagePreview
            root={completedOpfsRoot}
            fileName={fileName}
            fileSize={transferSize}
            onDownload={handleCompletedDownload}
            fallback={
              <DefaultAttachmentCard
                fileName={fileName}
                fileSize={transferSize}
                isFileReady={true}
                onDownload={handleCompletedDownload}
              />
            }
          />
        );
      }
      
      return (
        <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-surface-container-high shadow-sm">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-tertiary/10">
              <CheckIcon className="h-4 w-4 text-tertiary" />
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-medium text-on-surface">{fileName}</div>
              <div className="text-xs text-on-surface-variant">
                {formatBytes(transferSize)} <span className="text-tertiary">• Complete</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleCompletedDownload}
            className="flex items-center gap-1 px-3 py-1 bg-tertiary text-on-tertiary rounded-md hover:bg-tertiary/90 text-sm"
            title="Download to browser"
          >
            <DownloadIcon className="h-3 w-3" />
            Download
          </button>
        </div>
      );
    }
    
    // Show progress for other transferring states
    if (isTransferring) {
      const isGroup = !!transfer.metadata?.isGroupTransfer;
      const isSender = transfer.metadata?.direction === 'outbound';

      return (
        <div className="p-3 rounded-lg border bg-surface-container-high shadow-sm space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                <ExternalLinkIcon className="h-4 w-4 text-primary" />
              </div>
              <div className="flex flex-col">
                <div className="text-sm font-medium text-on-surface flex items-center gap-1.5">
                  {isGroup && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 leading-none">Group</span>}
                  {fileName}
                </div>
                <div className="text-xs text-on-surface-variant">
                  {formatBytes(file?.size || transfer.metadata?.size || 0)} • {getStateLabel(transfer.state)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {progress > 0 && (
                <div className="text-sm font-medium text-primary">{progress}%</div>
              )}
              {transfer.state === FileTransferStateEnum.TRANSFERRING && (
                <button
                  onClick={handlePauseTransfer}
                  className="flex items-center justify-center h-6 w-6 rounded hover:bg-surface-container-highest transition-colors"
                  title="Pause transfer"
                >
                  <PauseIcon className="h-3 w-3 text-on-surface-variant" />
                </button>
              )}
              <LoaderCircleIcon className="h-4 w-4 animate-spin text-primary" />
            </div>
          </div>

          {/* GROUP RECEIVER: Show sources (seeders contributing chunks) */}
          {isGroup && !isSender && transfer.metadata.groupSeeders && transfer.metadata.groupSeeders.size > 0 && (
            <div className="text-xs text-on-surface-variant pt-1 border-t border-outline-variant/30">
              <span className="font-medium">Sources: {transfer.metadata.groupSeeders.size}</span>
              {Array.from(transfer.metadata.groupSeeders.entries()).slice(0, 3).map(([addr, info]) => (
                <span key={addr} className="ml-2 text-on-surface-variant/70">
                  {peerNames[addr] || addr.slice(0, 8) + '…'} ({info.availableChunks?.length || 0} chunks)
                </span>
              ))}
            </div>
          )}

          {/* GROUP SENDER: Show per-receiver progress */}
          {isGroup && isSender && transfer.metadata.groupReceiverProgress && transfer.metadata.groupReceiverProgress.size > 0 && (
            <div className="text-xs pt-1 border-t border-outline-variant/30 space-y-1">
              <span className="font-medium text-on-surface-variant">Receivers:</span>
              {Array.from(transfer.metadata.groupReceiverProgress.entries()).map(([addr, acked]) => {
                const total = transfer.metadata.totalChunks || 1;
                const pct = Math.min(Math.round((acked / total) * 100), 100);
                return (
                  <div key={addr} className="flex items-center gap-2">
                    <span className="w-20 truncate text-on-surface-variant/70" title={addr}>{peerNames[addr] || addr.slice(0, 8) + '…'}</span>
                    <div className="flex-1 h-1.5 bg-outline-variant/20 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-8 text-right text-on-surface-variant">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
  }

  // Handle case where file metadata exists but no transfer record yet
  // This happens when:
  // 1. Receiver: Transfer hasn't been created yet (waiting for handshake)
  // 2. Sender: Transfer lookup failed (root hash mismatch)
  // 3. After browser refresh: in-memory transfer maps are cleared but DB persists
  //
  // FIX #6a: When file.downloaded === 1, the transfer is COMPLETE — fall through
  // to the completed UI (download card / inline image) instead of showing a spinner.
  // OPFS key = content hash = db.files.root, so download works without transfer record.
  if (file && !transfer && file.downloaded !== 1) {
    return (
      <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-surface-container-high shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-secondary/10">
            <ExternalLinkIcon className="h-4 w-4 text-secondary" />
          </div>
          <div className="flex flex-col">
            <div className="text-sm font-medium text-on-surface">{fileName}</div>
            <div className="text-xs text-on-surface-variant">
              {formatBytes(file.size || 0)} • Connecting...
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-secondary">
          <LoaderCircleIcon className="h-3 w-3 animate-spin" />
          <span>Waiting</span>
        </div>
      </div>
    );
  }

  // No file record at all - truly waiting for metadata
  if (!file) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg border bg-surface-container-high shadow-sm">
        <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-200">
          <ExternalLinkIcon className="h-4 w-4 text-gray-500" />
        </div>
        <div className="flex flex-col">
          <div className="text-sm font-medium text-on-surface">{fileName}</div>
          <div className="text-xs text-gray-500">Waiting for metadata...</div>
        </div>
      </div>
    );
  }

  // OPFS lookup key: prefer transfer.rootHash (Merkle root) over attachment root (content hash).
  // Chat message attachment uses SHA-256(file content) as entries key, but OPFS stores
  // files under the Merkle tree root hash. These are DIFFERENT hashes.
  const opfsRoot = transfer?.rootHash || root;

  // Download handler - only works when file is ready
  const handleDownload = async () => {
    if (!isFileReady || !file) {
      console.warn('[AttachmentCard] Cannot download - file not ready');
      return;
    }
    
    try {
      const opfsFile = await getFileFromOPFS(opfsRoot);
      if (!opfsFile) {
        console.error('[AttachmentCard] File not found in OPFS', { opfsRoot: opfsRoot.slice(0, 8), attachmentRoot: root.slice(0, 8) });
        return;
      }
      
      const url = URL.createObjectURL(opfsFile);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`[AttachmentCard] Download failed:`, error);
    }
  };

  // Check if this image should render inline
  // For inbound: check per-peer setting using senderAddress (the peer who sent it)
  // For own messages: check per-peer setting using chatAddress (the peer we're chatting with)
  // OPFS lookup uses opfsRoot (transfer.rootHash) so both sides resolve correctly.
  const shouldRenderImage = isImageFile(fileName) && isFileReady && peerRendersImages;

  // Inline image rendering for completed image transfers
  if (shouldRenderImage) {
    return (
      <InlineImagePreview
        root={opfsRoot}
        fileName={fileName}
        fileSize={file.size || 0}
        onDownload={handleDownload}
        fallback={
          <DefaultAttachmentCard
            fileName={fileName}
            fileSize={file.size || 0}
            isFileReady={isFileReady}
            onDownload={handleDownload}
          />
        }
      />
    );
  }

  // GROUP SENDER COMPLETED: Show per-receiver progress on the completed card
  // Sender is COMPLETED immediately in pull model — receivers pull at their own pace
  if (transfer?.metadata?.isGroupTransfer && transfer.metadata?.direction === 'outbound') {
    const totalChunks = transfer.metadata.totalChunks || 1;
    return (
      <div className="rounded-lg border bg-surface-container-high p-3 shadow-sm space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-tertiary/10">
              <CheckIcon className="h-4 w-4 text-tertiary" />
            </div>
            <div className="flex flex-col">
              <div className="text-sm font-medium text-on-surface flex items-center gap-1.5">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 leading-none">Group</span>
                {fileName}
              </div>
              <div className="text-xs text-on-surface-variant">
                {formatBytes(file.size || 0)} <span className="text-tertiary">• Serving</span>
              </div>
            </div>
          </div>
        </div>
        {transfer.metadata.groupReceiverProgress && transfer.metadata.groupReceiverProgress.size > 0 ? (
          <div className="text-xs pt-1 border-t border-outline-variant/30 space-y-1">
            <span className="font-medium text-on-surface-variant">Receivers:</span>
            {Array.from(transfer.metadata.groupReceiverProgress.entries()).map(([addr, acked]) => {
              const pct = Math.min(Math.round((acked / totalChunks) * 100), 100);
              return (
                <div key={addr} className="flex items-center gap-2">
                  <span className="w-20 truncate text-on-surface-variant/70" title={addr}>{peerNames[addr] || addr.slice(0, 8) + '…'}</span>
                  <div className="flex-1 h-1.5 bg-outline-variant/20 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-tertiary' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-right text-on-surface-variant">{pct}%</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs pt-1 border-t border-outline-variant/30">
            <span className="text-on-surface-variant">Waiting for receivers…</span>
          </div>
        )}
      </div>
    );
  }

  // GROUP RECEIVER COMPLETED: Show group-branded card with download button
  if (transfer?.metadata?.isGroupTransfer && transfer.metadata?.direction === 'inbound' && isFileReady) {
    return (
      <div className="group relative flex items-center justify-between gap-2 rounded-lg border bg-surface-container-high p-3 shadow-sm hover:bg-accent/5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-tertiary/10">
            <CheckIcon className="h-4 w-4 text-tertiary" />
          </div>
          <div className="flex flex-col">
            <div className="text-sm font-medium text-on-surface flex items-center gap-1.5">
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 leading-none">Group</span>
              {fileName}
            </div>
            <div className="flex items-center gap-1 text-xs text-on-surface-variant">
              <span>{formatBytes(file.size || 0)}</span>
              <span className="text-tertiary">• Complete</span>
            </div>
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1 px-3 py-1 bg-tertiary text-on-tertiary rounded-md hover:bg-tertiary/90 text-sm transition-opacity"
          title="Download to browser"
        >
          <DownloadIcon className="h-3 w-3" />
          Download
        </button>
      </div>
    );
  }

  // Default attachment card — file download UI
  return (
    <DefaultAttachmentCard
      fileName={fileName}
      fileSize={file.size || 0}
      isFileReady={isFileReady}
      onDownload={handleDownload}
    />
  );
}

function DefaultAttachmentCard({
  fileName,
  fileSize,
  isFileReady,
  onDownload,
}: {
  fileName: string;
  fileSize: number;
  isFileReady: boolean;
  onDownload: () => void;
}) {
  return (
    <div className="group relative flex items-center justify-between gap-2 rounded-lg border bg-surface-container-high p-3 shadow-sm hover:bg-accent/5">
      <div className="flex items-center gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded ${isFileReady ? 'bg-tertiary/10' : 'bg-orange-100'}`}>
          {isFileReady ? (
            <CheckIcon className="h-4 w-4 text-tertiary" />
          ) : (
            <LoaderCircleIcon className="h-4 w-4 animate-spin text-orange-600" />
          )}
        </div>
        <div className="flex flex-col">
          <div className="text-sm font-medium text-on-surface">
            {fileName}
          </div>
          <div className="flex items-center gap-1 text-xs text-on-surface-variant">
            <span>{formatBytes(fileSize)}</span>
            {isFileReady ? (
              <span className="text-tertiary">• Complete</span>
            ) : (
              <span className="text-orange-600">• Preparing...</span>
            )}
          </div>
        </div>
      </div>

      {isFileReady && (
        <button
          onClick={onDownload}
          className="flex items-center gap-1 px-3 py-1 bg-tertiary text-on-tertiary rounded-md hover:bg-tertiary/90 text-sm transition-opacity"
          title="Download to browser"
        >
          <DownloadIcon className="h-3 w-3" />
          Download
        </button>
      )}
      
      {!isFileReady && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <LoaderCircleIcon className="h-3 w-3 animate-spin" />
          <span>Processing...</span>
        </div>
      )}
    </div>
  );
}

/**
 * Inline image preview for completed image file transfers.
 * Loads image from OPFS, manages blob URL lifecycle, and provides
 * click-to-expand lightbox with download capability.
 */
function InlineImagePreview({
  root,
  fileName,
  fileSize,
  onDownload,
  fallback,
}: {
  root: string;
  fileName: string;
  fileSize: number;
  onDownload: () => void;
  fallback: React.ReactNode;
}) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  // FIX #6b: Counter that triggers re-fetch from OPFS on visibility recovery
  const [retryKey, setRetryKey] = React.useState(0);

  // FIX #6b: Re-render image when tab becomes visible after being hidden.
  // Handles: GPU reclaim, blob GC, OPFS race during hidden-state transfer completion.
  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && error) {
        console.debug('[InlineImagePreview] Tab visible + image errored, retrying OPFS load', { root: root.slice(0, 12) });
        setError(false);
        setLoading(true);
        setBlobUrl(null);
        setRetryKey(k => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [error, root]);

  // Load image from OPFS and create blob URL
  // Retries up to 3 times with 500ms delay to handle race between
  // DB downloaded=1 update and OPFS write completion.
  React.useEffect(() => {
    let revoked = false;
    let url: string | null = null;

    (async () => {
      const MAX_RETRIES = 3;
      const RETRY_DELAY_MS = 500;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (revoked) return;

        try {
          const opfsFile = await getFileFromOPFS(root);
          if (revoked) return;

          if (opfsFile) {
            // FIX 9a: Read actual bytes and create a typed Blob with explicit MIME type.
            // OPFS File objects have type: "" (no MIME type). Firefox refuses to render
            // blob URLs with empty content-type in <img> tags. Reading the ArrayBuffer
            // also forces a real read (bypasses any stale File handle from OPFS
            // finalization races where writer close and getFile() overlap).
            const buffer = await opfsFile.arrayBuffer();
            if (revoked) return;
            if (buffer.byteLength === 0) {
              // OPFS file exists but is empty — writer may still be flushing
              console.debug('[InlineImagePreview] OPFS file empty (writer may still be flushing)', { root: root.slice(0, 12), attempt });
              if (attempt < MAX_RETRIES) {
                await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
                continue;
              }
            }
            const mimeType = getImageMimeType(fileName);
            const typedBlob = new Blob([buffer], { type: mimeType });
            url = URL.createObjectURL(typedBlob);
            setBlobUrl(url);
            setLoading(false);
            return; // success
          }

          // File not in OPFS yet — retry after delay
          if (attempt < MAX_RETRIES) {
            console.debug(`[InlineImagePreview] OPFS file not found (attempt ${attempt + 1}/${MAX_RETRIES + 1}), retrying...`, { root: root.slice(0, 12) });
            await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
          }
        } catch (err) {
          console.warn('[InlineImagePreview] Failed to load image from OPFS:', err);
          if (!revoked) {
            setError(true);
            setLoading(false);
          }
          return;
        }
      }

      // All retries exhausted — show fallback card instead of infinite spinner
      if (!revoked) {
        console.warn('[InlineImagePreview] OPFS file not found after retries, falling back', { root: root.slice(0, 12) });
        setError(true);
        setLoading(false);
      }
    })();

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [root, retryKey]);

  // Error fallback — show standard download card
  if (error) {
    return <>{fallback}</>;
  }

  // Loading state
  if (loading) {
    return (
      <div className="mb-1 rounded-lg border bg-surface-container-high overflow-hidden">
        <div className="flex items-center justify-center h-40 bg-surface-container animate-pulse">
          <LoaderCircleIcon className="h-6 w-6 animate-spin text-on-surface-variant/50" />
        </div>
        <div className="flex items-center justify-between px-3 py-1.5">
          <span className="text-xs text-on-surface-variant truncate">{fileName}</span>
          <span className="text-xs text-on-surface-variant">{formatBytes(fileSize)}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-1 rounded-lg border bg-surface-container-high overflow-hidden">
        {/* Clickable image */}
        <button
          type="button"
          className="block w-full cursor-pointer focus:outline-none"
          onClick={() => setLightboxOpen(true)}
          title="Click to expand"
        >
          <img
            src={blobUrl!}
            alt={fileName}
            className="w-full max-h-80 object-contain bg-black/5"
            loading="lazy"
            onError={() => {
              console.debug('[InlineImagePreview] <img> onError — blob may be stale or GPU-reclaimed', { root: root.slice(0, 12), retryKey });
              if (blobUrl) URL.revokeObjectURL(blobUrl);
              // FIX #6b: Attempt OPFS re-fetch, but cap at 3 retries to prevent infinite loops
              // (e.g. genuinely corrupted file). After cap, error becomes permanent → fallback card.
              if (retryKey < 3) {
                setBlobUrl(null);
                setLoading(true);
                setRetryKey(k => k + 1);
              } else {
                setError(true);
              }
            }}
          />
        </button>
        {/* Info bar with filename, size, download */}
        <div className="flex items-center justify-between px-3 py-1.5">
          <span className="text-xs text-on-surface-variant truncate mr-2">{fileName}</span>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-on-surface-variant">{formatBytes(fileSize)}</span>
            <button
              onClick={onDownload}
              className="flex items-center gap-1 px-2 py-0.5 text-xs bg-tertiary/10 text-tertiary rounded hover:bg-tertiary/20 transition-colors"
              title="Download to browser"
            >
              <DownloadIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && blobUrl && (
        <ImageLightbox
          src={blobUrl}
          fileName={fileName}
          fileSize={fileSize}
          onClose={() => setLightboxOpen(false)}
          onDownload={onDownload}
        />
      )}
    </>
  );
}

/**
 * Full-screen image lightbox overlay.
 * Closes on backdrop click, Escape key, or close button.
 */
function ImageLightbox({
  src,
  fileName,
  fileSize,
  onClose,
  onDownload,
}: {
  src: string;
  fileName: string;
  fileSize: number;
  onClose: () => void;
  onDownload: () => void;
}) {
  // Close on Escape
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-white text-sm truncate mr-4">
          {fileName} <span className="text-white/60 ml-1">{formatBytes(fileSize)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-white/15 rounded-lg hover:bg-white/25 transition-colors"
            title="Download"
          >
            <DownloadIcon className="h-4 w-4" />
            Download
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-8 w-8 text-white bg-white/15 rounded-lg hover:bg-white/25 transition-colors"
            title="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Image */}
      <img
        src={src}
        alt={fileName}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function GroupInviteCard({
  groupInvite,
}: {
  groupInvite: NonNullable<MessageEntity["groupInvite"]>;
}) {
  const db = useMobyDB();
  const node = useMobyNodeClient();
  const session = useSession();
  const navigate = useLocation()[1];

  const group = useSuspenseLiveQuery(async () => {
    return db.chatByGroupAddress(groupInvite.groupAddress);
  }, [groupInvite.groupAddress]);

  if (session === undefined) {
    return null;
  }

  const onJoinGroup = async () => {
    if (group !== undefined) {
      return;
    }

    const peer = node.getOrCreatePeer(groupInvite.inviterAddress);
    if (peer.connection.connectionState !== "connected") {
      toast.error(
        "The person that invited you to the group is not online. Please try again later.",
      );
      return;
    }

    const response = await peer.request({
      type: MessagePacketType.GROUP_DETAILS_REQUEST,
      groupAddress: hexToBytes(groupInvite.groupAddress),
    });

    if (response.type !== MessagePacketType.GROUP_DETAILS_RESPONSE) {
      toast.error("Unable to join group. Please try again later.");
      return;
    }

    if (response.group === undefined) {
      toast.error(
        "The person that invited you to the group is not a member of the group.",
      );
      return;
    }

    const join: bin.Parsed<typeof WiredMessageBody> = {
      type: MessageBodyType.GROUP_JOIN,
      groupAddress: hexToBytes(groupInvite.groupAddress),
      groupInvite: {
        inviterSignature: hexToBytes(groupInvite.inviterSignature),
        body: {
          groupAddress: hexToBytes(groupInvite.groupAddress),
          inviterAddress: hexToBytes(groupInvite.inviterAddress),
          inviteeAddress: hexToBytes(session.address),
        },
      },
      from: {
        address: hexToBytes(session.address),
        nonce: 1,
      },
      version: (response.group.version ?? 0) + 1,
      timestamp: BigInt(Date.now()),
    };

    const message = {
      signature: ed25519.sign(
        binencode(WiredMessageBody, join),
        hexToBytes(session.secret),
      ),
      body: join,
    } satisfies bin.Parsed<typeof WiredMessage>;

    const peerAddresses = response.group.peers.map(bytesToHex);

    await db.transaction("rw", db.chats, db.messages, async () => {
      await db.chats.add({
        address: groupInvite.groupAddress,
        type: MessageDestinationType.GROUP,
        name: response.group?.name,
        peers: peerAddresses,
        nonces: {},
      });

      await db.storeGroupJoinMessage(session.address, message);
    });

    for (const peerAddress of peerAddresses) {
      const peer = node.getOrCreatePeer(peerAddress);
      if (peer.connection.connectionState !== "connected") {
        continue;
      }
      void peer.chat.send(
        binencode(WiredMessagePacket, {
          type: MessagePacketType.MESSAGE,
          message,
        }),
      );
    }

    toast.success("You have joined the group!");

    navigate(`/chats/${groupInvite.groupAddress}`);
  };

  return (
    <div className="rounded-medium text-on-surface group-data-user:bg-surface-container-highest bg-surface-container-high mb-1 p-3">
      <div className="flex items-center gap-4">
        <button
          disabled={group !== undefined}
          className="group/join bg-surface text-primary rounded-small relative grid size-12 shrink-0 place-items-center overflow-hidden hover:cursor-pointer disabled:pointer-events-none"
          onClick={onJoinGroup}
        >
          <span
            aria-hidden
            className="group-hover/join:bg-on-primary-container/8 group-active/join:bg-on-primary-container/10 rounded-small absolute inset-0 transition"
          />
          {group === undefined && <PlusIcon size={24} />}
          {group !== undefined && <CheckIcon size={24} />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-title-md text-on-primary-container truncate">
            Group Invite
          </div>
          <div className="text-label-md text-on-surface-variant truncate font-mono select-all">
            {groupInvite.groupAddress}
          </div>
        </div>
      </div>
    </div>
  );
}
